/**
 * useSSE — hook para eventos en tiempo real vía Server-Sent Events.
 *
 * Uso:
 *   useSSE({
 *     logoTipo: "mi_empresa",   // slug de la empresa activa
 *     token: authToken,         // JWT del usuario
 *     onEvent: (event) => {     // callback al recibir un evento
 *       // event = { tipo: "factura_actualizada", data: { id, estado } }
 *     },
 *     enabled: true,            // false para deshabilitar (ej. usuario no logueado)
 *   });
 *
 * Eventos emitidos por el backend:
 *   factura_actualizada   — { id, estado }
 *   ticket_nuevo          — { id, numero, titulo, empresa_id, prioridad }
 *   ticket_actualizado    — { id, estado, historial_count? }
 *   cheque_actualizado    — { id, estado }
 *   reserva_actualizada   — { id, estado }
 *
 * El hook reconecta automáticamente si la conexión se corta.
 * No necesita limpieza manual — se cierra al desmontar el componente.
 */
import { useEffect, useRef, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const RECONNECT_DELAY_MS = 5000;

export function useSSE({ logoTipo, token, onEvent, enabled = true }) {
  const esRef = useRef(null);
  const onEventRef = useRef(onEvent);
  const reconnectTimer = useRef(null);
  const destroyed = useRef(false);

  // Mantener la referencia del callback actualizada sin reconectar
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  const connect = useCallback(() => {
    if (destroyed.current || !logoTipo || !token) return;

    const url = `${API}/api/admin/events?logo_tipo=${encodeURIComponent(logoTipo)}&token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    esRef.current = es;

    es.onmessage = (e) => {
      try {
        const event = JSON.parse(e.data);
        if (onEventRef.current) onEventRef.current(event);
      } catch {
        // ignorar mensajes malformados
      }
    };

    es.onerror = () => {
      es.close();
      esRef.current = null;
      if (!destroyed.current) {
        // Reconectar tras 5 segundos
        reconnectTimer.current = setTimeout(connect, RECONNECT_DELAY_MS);
      }
    };
  }, [logoTipo, token]); // eslint-disable-line

  useEffect(() => {
    if (!enabled || !logoTipo || !token) return;
    destroyed.current = false;
    connect();

    return () => {
      destroyed.current = true;
      clearTimeout(reconnectTimer.current);
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
    };
  }, [enabled, logoTipo, token, connect]);
}
